import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeIn, GoldButton } from "@/components/Reveal";
import { GROUPS } from "@/data/groups";
import { PACKAGES } from "@/data/island";

export default function GroupPage() {
  const { slug } = useParams();
  const group = GROUPS.find((g) => g.slug === slug);
  if (!group) return <Navigate to="/group-packages" replace />;

  const others = GROUPS.filter((g) => g.slug !== slug);

  return (
    <>
      <PageHero eyebrow={group.tag} title={group.title} image={group.heroImage}>
        {group.short}
      </PageHero>

      <section className="bg-navy-800 py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Eyebrow>The Occasion</Eyebrow>
            <div className="mt-8 space-y-6">
              {group.paragraphs.map((p, i) => (
                <FadeIn key={i} delay={i * 0.05}>
                  <p className="text-sm font-light leading-relaxed text-mist sm:text-base">{p}</p>
                </FadeIn>
              ))}
            </div>

            <FadeIn delay={0.1}>
              <div className="mt-14">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.26em] text-gold">
                  What's Included
                </h3>
                <ul className="mt-6 grid gap-4 sm:grid-cols-2">
                  {group.highlights.map((h) => (
                    <li
                      key={h}
                      className="flex items-start gap-3 border border-white/10 bg-navy-900/50 p-5 text-sm text-ivory/90"
                    >
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>

          <div>
            <FadeIn delay={0.15}>
              <div className="border border-gold/30 bg-navy-900/70 p-8 lg:sticky lg:top-28" data-testid="group-price-card">
                <h2 className="font-serif text-3xl text-ivory">Group Package</h2>
                <p className="mt-6 font-serif text-5xl text-gold">{group.price}</p>
                <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-mist">{group.capacity}</p>
                <GoldButton href="/contact" testId={`group-enquire-${group.slug}`} className="mt-8 w-full">
                  Enquire Now
                </GoldButton>
                <a
                  href="tel:+353862518252"
                  data-testid={`group-call-${group.slug}`}
                  className="mt-4 flex items-center justify-center gap-2 border border-gold/50 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold-muted"
                >
                  <Phone className="h-3.5 w-3.5" /> +353 86 251 8252
                </a>
                <p className="mt-6 text-[12px] font-light leading-relaxed text-mist">
                  Every package is personalised &mdash; we will create a magical all-inclusive
                  package for your event.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-navy-900 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <FadeIn>
              <img
                src={group.bodyImage}
                alt={group.title}
                data-testid="group-body-image"
                className="aspect-[4/3] w-full border border-white/10 object-cover"
              />
            </FadeIn>
            <FadeIn delay={0.1}>
              <Eyebrow>Reserve Your Stay</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl font-medium leading-tight text-ivory sm:text-5xl">
                Bespoke <em className="italic text-gold">group packages</em>
              </h2>
              <p className="mt-6 text-sm font-light leading-relaxed text-mist sm:text-base">
                Reserve your stay now with a bespoke group package. Contact us today to discuss
                customised packages, including activity options, meal plans, transportation and
                accommodation.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <GoldButton href="/contact" testId={`group-cta-${group.slug}`}>
                  Plan Your Day
                </GoldButton>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="bg-navy-950 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Eyebrow>Other Packages</Eyebrow>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {others.map((o, i) => (
              <FadeIn key={o.slug} delay={i * 0.1} className="h-full">
                <Link
                  to={`/groups/${o.slug}`}
                  data-testid={`other-group-${o.slug}`}
                  className="group relative block h-full min-h-[280px] overflow-hidden border border-white/5"
                >
                  <img
                    src={o.heroImage}
                    alt={o.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.26em] text-gold">{o.tag}</p>
                      <h3 className="mt-2 font-serif text-2xl text-ivory">{o.title}</h3>
                    </div>
                    <ArrowRight className="h-5 w-5 shrink-0 text-gold transition-transform duration-300 group-hover:translate-x-1.5" />
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.2}>
            <Link
              to="/group-packages"
              data-testid="back-to-packages"
              className="mt-12 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-mist transition-colors hover:text-gold"
            >
              All Group Packages <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
